import { googleReverseGeocode } from '@rsos/components/capstone/common/Map/utils';
import {
  isGoogleGeocoderReady,
  MapsNotReadyError,
} from './googleGeocoderReady';

export interface GoogleReverseGeocodeLocationArgs {
  latitude: number;
  longitude: number;
  /** Toggles the "is reverse geocoding" state while the request is in flight. */
  onReverseGeocoding?: (isReverseGeocoding: boolean) => void;
}

export interface GoogleReverseGeocodeResult {
  address: string;
  addressObject: google.maps.GeocoderAddressComponent[] | null;
}

/**
 * Reverse geocode coordinates using the Google reverse geocode service. Returns
 * the formatted address and its address components, or rejects with a
 * {@link MapsNotReadyError} when the Maps Geocoder has not loaded yet.
 */
const googleReverseGeocodeLocation = ({
  latitude,
  longitude,
  onReverseGeocoding,
}: GoogleReverseGeocodeLocationArgs): Promise<GoogleReverseGeocodeResult> => {
  // Bail out before constructing a Geocoder if Maps hasn't loaded one yet. The
  // Maps JS API only loads once the user opens the Google map, so an earlier
  // reverse geocode legitimately has no Geocoder available. Surface a typed
  // error the caller can treat as expected rather than letting
  // `new google.maps.Geocoder()` throw an opaque runtime error.
  if (!isGoogleGeocoderReady()) {
    return Promise.reject(new MapsNotReadyError());
  }

  const latlng = { lat: latitude, lng: longitude };

  if (onReverseGeocoding) onReverseGeocoding(true);

  return googleReverseGeocode(latlng)
    .then((response: google.maps.GeocoderResult[]) => {
      const address = response?.[0]?.formatted_address ?? '';
      const addressObject = response?.[0]?.address_components ?? null;

      return { address, addressObject };
    })
    .finally(() => {
      if (onReverseGeocoding) onReverseGeocoding(false);
    });
};

export default googleReverseGeocodeLocation;
